import { useEffect, useState } from "react";
import { Seo } from "@/lib/seo";
import { Copy, Check, Loader2, Lock, ArrowLeft, Sparkles, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOMENTOS = ["Vida independiente", "Vida en pareja", "Hijos y crianza", "Vivienda y patrimonio", "Trabajo y negocio", "Cambios de vida", "Cuidados y autonomía", "Legado y sucesión"];
const SERVICIOS = ["Asesoría de Negocios", "Bienes y Patrimonio", "Familia y Acuerdos", "Defensa Laboral", "Seguros y Siniestros", "Protección del Consumidor", "Protección Digital"];
const CANALES = ["instagram", "facebook", "linkedin", "web", "newsletter", "whatsapp"];
const FORMATOS = ["carrusel", "video", "post", "articulo_web", "newsletter"];
const PUBLICOS: Record<string, string> = {
  A: "Personas que comienzan una vida independiente",
  B: "Parejas que organizan convivencia, bienes o responsabilidades",
  C: "Madres, padres y personas responsables de crianza",
  D: "Propietarios, arrendadores, inquilinos y compradores",
  E: "Trabajadores y profesionistas independientes",
  F: "Emprendedores y titulares de negocios familiares",
  G: "Personas que atraviesan separaciones o cambios patrimoniales",
  H: "Personas cuidadoras, adultos mayores y personas que requieren apoyos",
  I: "Personas que preparan una sucesión o enfrentan un fallecimiento",
  J: "Consumidores o asegurados ante incumplimientos",
  K: "Personas con incidentes de identidad, datos o reputación digital",
};
const OBJETIVOS = ["comunidad", "educación", "reconocimiento", "consultas", "continuidad"];
const ESTADOS = ["IDEA", "BORRADOR", "PENDIENTE_DE_FUENTES", "PENDIENTE_DE_REVISION_JURIDICA", "PENDIENTE_DE_VALIDACION_COMERCIAL", "LISTO_PARA_APROBACION", "APROBADO", "REQUIERE_ACTUALIZACION", "RETIRADO"];

const ESTADO_ESTILO: Record<string, string> = {
  IDEA: "bg-parchment-surface text-ink-muted",
  BORRADOR: "bg-parchment-surface text-ink-text",
  PENDIENTE_DE_FUENTES: "bg-azul-light text-azul",
  PENDIENTE_DE_REVISION_JURIDICA: "bg-azul-light text-azul",
  PENDIENTE_DE_VALIDACION_COMERCIAL: "bg-azul-light text-azul",
  LISTO_PARA_APROBACION: "bg-salvia-light text-salvia-dark",
  APROBADO: "bg-salvia-light text-salvia-dark border border-salvia-marco",
  REQUIERE_ACTUALIZACION: "bg-destructive/10 text-destructive",
  RETIRADO: "bg-destructive/10 text-destructive",
};

interface Pieza {
  id: string; lote_id: string; tema: string; gancho: string; canal: string; formato: string;
  publico: string; momento_de_vida: string; area_de_servicio: string; objetivo: string;
  texto_publicacion: string; laminas: string[]; guion: string[];
  cta: { texto: string; url: string | null; destino_verificado: boolean };
  visual: { descripcion: string; texto_alternativo: string };
  seo: { slug: string | null; meta_description: string | null; intencion_busqueda: string | null };
  fuentes: string[]; verificacion: { jurisdiccion: string | null; fecha: string | null; afirmaciones_pendientes: string[] };
  oferta_confirmada: boolean; estado: string;
  aprobacion: { responsable: string | null; fecha: string | null; version_aprobada: string | null };
  historial: { de: string; a: string; nota: string; responsable: string; fecha: string }[];
  created_at: string; updated_at: string;
}

const vacia: Pieza = {
  id: "", lote_id: "", tema: "", gancho: "", canal: "", formato: "", publico: "", momento_de_vida: "",
  area_de_servicio: "", objetivo: "", texto_publicacion: "", laminas: [], guion: [],
  cta: { texto: "", url: null, destino_verificado: false },
  visual: { descripcion: "", texto_alternativo: "" },
  seo: { slug: null, meta_description: null, intencion_busqueda: null },
  fuentes: [], verificacion: { jurisdiccion: "México", fecha: null, afirmaciones_pendientes: [] },
  oferta_confirmada: false, estado: "BORRADOR",
  aprobacion: { responsable: null, fecha: null, version_aprobada: null },
  historial: [], created_at: "", updated_at: "",
};

function Badge({ estado }: { estado: string }) {
  return (
    <span className={`inline-block rounded-full px-3 py-1 font-mono text-[10px] tracking-wide ${ESTADO_ESTILO[estado] || "bg-parchment-surface text-ink-muted"}`} data-testid={`estudio-estado-${estado}`}>
      {estado.replaceAll("_", " ")}
    </span>
  );
}

function Chip({ label, activo, onClick, testId }: { label: string; activo: boolean; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${
        activo ? "border-azul bg-azul text-white" : "border-hairline bg-white text-ink-text hover:border-azul/50"
      }`}
    >
      {label}
    </button>
  );
}

export default function Estudio() {
  const [token, setToken] = useState(sessionStorage.getItem("estudio_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [validado, setValidado] = useState(false);
  const [tab, setTab] = useState<"encargo" | "bandeja">("encargo");

  const [objetivo, setObjetivo] = useState("educación");
  const [canales, setCanales] = useState<string[]>(["instagram"]);
  const [publico, setPublico] = useState("A");
  const [momentos, setMomentos] = useState<string[]>([]);
  const [servicios, setServicios] = useState<string[]>([]);
  const [formatos, setFormatos] = useState<string[]>(["carrusel"]);
  const [volumen, setVolumen] = useState(4);
  const [ofertas, setOfertas] = useState("");
  const [notas, setNotas] = useState("");

  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState("");
  const [supuestos, setSupuestos] = useState<string[]>([]);
  const [piezas, setPiezas] = useState<Pieza[]>([]);
  const [sel, setSel] = useState<Pieza | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [nota, setNota] = useState("");
  const [responsable, setResponsable] = useState("");
  const [guardando, setGuardando] = useState(false);

  const headers = { "X-Admin-Token": token, "Content-Type": "application/json" };

  async function verificarToken(t: string) {
    const res = await fetch(`${API}/contenido/piezas`, { headers: { "X-Admin-Token": t } });
    if (res.ok) {
      sessionStorage.setItem("estudio_token", t);
      setToken(t);
      setValidado(true);
      await cargarPiezas(t);
    } else {
      setError("Token incorrecto. Pídeselo al responsable del despacho.");
    }
  }

  async function cargarPiezas(t = token) {
    const res = await fetch(`${API}/contenido/piezas${filtroEstado ? `?estado=${filtroEstado}` : ""}`, { headers: { "X-Admin-Token": t } });
    if (res.ok) setPiezas(await res.json());
  }

  useEffect(() => {
    if (validado) cargarPiezas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstado]);

  async function generar() {
    setGenerando(true);
    setError("");
    setSel(null);
    try {
      const res = await fetch(`${API}/contenido/generar`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          objetivo, canales, publico, momentos, servicios, formatos,
          volumen: Number(volumen) || 4, ofertas_confirmadas: ofertas, notas,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.detail || "No pudimos generar el lote. Intenta de nuevo.");
      } else {
        setSupuestos(data.supuestos || []);
        await cargarPiezas();
        setTab("bandeja");
      }
    } catch {
      setError("Falló la conexión con el servidor de generación.");
    } finally {
      setGenerando(false);
    }
  }

  async function cambiarEstado(estado: string) {
    if (!sel) return;
    if (estado === "APROBADO" && !responsable.trim()) {
      setError("Para aprobar, escribe el nombre del responsable humano.");
      return;
    }
    setGuardando(true);
    setError("");
    try {
      const res = await fetch(`${API}/contenido/piezas/${sel.id}/estado`, {
        method: "POST", headers,
        body: JSON.stringify({ estado, nota, responsable }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) setError(data?.detail || "No pudimos actualizar el estado.");
      else { setSel(data); setNota(""); }
    } catch {
      setError("Falló la conexión.");
    } finally {
      setGuardando(false);
    }
  }

  function copiar() {
    if (!sel) return;
    const partes = [sel.texto_publicacion];
    if (sel.laminas.length) partes.push("LÁMINAS:\n" + sel.laminas.join("\n"));
    if (sel.guion.length) partes.push("GUION:\n" + sel.guion.join("\n"));
    if (sel.cta.texto) partes.push(`CTA: ${sel.cta.texto} → ${sel.cta.url || "sin destino"}`);
    navigator.clipboard.writeText(partes.join("\n\n"));
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  const alternar = (lista: string[], setLista: (v: string[]) => void, valor: string) =>
    setLista(lista.includes(valor) ? lista.filter((x) => x !== valor) : [...lista, valor]);

  if (!validado) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-parchment px-4">
        <Seo title="Estudio interno" description="Panel interno de contenido." path="/estudio" noindex />
        <div className="w-full max-w-md border border-hairline bg-white p-8">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-parchment-surface text-azul">
            <Lock className="h-5 w-5" />
          </span>
          <h1 className="mt-5 font-serif text-3xl text-ink-deep">Estudio de contenido</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Panel interno de Tulevik Legal. Solo para el equipo de validación.
          </p>
          <input
            type="password"
            data-testid="estudio-token-input"
            className="field mt-6"
            placeholder="Token del panel"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verificarToken(tokenInput.trim())}
          />
          <button
            type="button"
            data-testid="estudio-token-button"
            className="btn-primary mt-4 w-full"
            onClick={() => verificarToken(tokenInput.trim())}
          >
            Entrar
          </button>
          {error && <p className="mt-4 text-sm text-destructive" data-testid="estudio-error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-parchment">
      <Seo title="Estudio interno" description="Panel interno de contenido." path="/estudio" noindex />
      <header className="sticky top-0 z-40 border-b border-hairline bg-parchment/90 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/emblema-tulevik.png" alt="" className="h-9 w-9" />
            <div>
              <p className="font-brand text-sm tracking-[0.14em] text-azul">TULEVIK LEGAL</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-salvia">Estudio de contenido</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="estudio-tab-encargo"
              onClick={() => setTab("encargo")}
              className={`rounded-md px-4 py-2 text-sm font-medium ${tab === "encargo" ? "bg-azul text-white" : "text-ink-text hover:bg-parchment-surface"}`}
            >
              Nuevo encargo
            </button>
            <button
              type="button"
              data-testid="estudio-tab-bandeja"
              onClick={() => setTab("bandeja")}
              className={`rounded-md px-4 py-2 text-sm font-medium ${tab === "bandeja" ? "bg-azul text-white" : "text-ink-text hover:bg-parchment-surface"}`}
            >
              Bandeja ({piezas.length})
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        {tab === "encargo" && (
          <div data-testid="estudio-encargo">
            <h1 className="font-serif text-3xl text-ink-deep sm:text-4xl">Nuevo encargo editorial</h1>
            <p className="mt-2 max-w-2xl text-sm text-ink-muted">
              El motor genera piezas completas como BORRADORES con el Prompt Maestro del despacho. Nada se aprueba ni publica automáticamente: todo llega aquí y a los correos de validación para revisión humana.
            </p>

            <div className="mt-8 grid gap-6 border border-hairline bg-white p-6 sm:p-8 lg:grid-cols-2">
              <div>
                <label className="field-label" htmlFor="estudio-objetivo">Objetivo</label>
                <select id="estudio-objetivo" data-testid="estudio-objetivo-select" className="field" value={objetivo} onChange={(e) => setObjetivo(e.target.value)}>
                  {OBJETIVOS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="estudio-publico">Público principal</label>
                <select id="estudio-publico" data-testid="estudio-publico-select" className="field" value={publico} onChange={(e) => setPublico(e.target.value)}>
                  {Object.entries(PUBLICOS).map(([k, v]) => <option key={k} value={k}>{k}. {v}</option>)}
                </select>
              </div>

              <div className="lg:col-span-2">
                <p className="field-label">Canales</p>
                <div className="flex flex-wrap gap-2">
                  {CANALES.map((c) => <Chip key={c} label={c} testId={`estudio-canal-${c}`} activo={canales.includes(c)} onClick={() => alternar(canales, setCanales, c)} />)}
                </div>
              </div>
              <div className="lg:col-span-2">
                <p className="field-label">Momentos de vida (opcional)</p>
                <div className="flex flex-wrap gap-2">
                  {MOMENTOS.map((m) => <Chip key={m} label={m} testId={`estudio-momento-${m.toLowerCase().replaceAll(" ", "-")}`} activo={momentos.includes(m)} onClick={() => alternar(momentos, setMomentos, m)} />)}
                </div>
              </div>
              <div className="lg:col-span-2">
                <p className="field-label">Áreas de servicio (opcional)</p>
                <div className="flex flex-wrap gap-2">
                  {SERVICIOS.map((s) => <Chip key={s} label={s} testId={`estudio-servicio-${s.toLowerCase().replaceAll(" ", "-")}`} activo={servicios.includes(s)} onClick={() => alternar(servicios, setServicios, s)} />)}
                </div>
              </div>
              <div className="lg:col-span-2">
                <p className="field-label">Formatos</p>
                <div className="flex flex-wrap gap-2">
                  {FORMATOS.map((f) => <Chip key={f} label={f} testId={`estudio-formato-${f}`} activo={formatos.includes(f)} onClick={() => alternar(formatos, setFormatos, f)} />)}
                </div>
              </div>

              <div>
                <label className="field-label" htmlFor="estudio-volumen">Número de piezas (1–8)</label>
                <input id="estudio-volumen" type="number" min={1} max={8} data-testid="estudio-volumen-input" className="field" value={volumen} onChange={(e) => setVolumen(Number(e.target.value))} />
              </div>
              <div>
                <label className="field-label" htmlFor="estudio-ofertas">Ofertas confirmadas para este encargo (opcional)</label>
                <input id="estudio-ofertas" data-testid="estudio-ofertas-input" className="field" placeholder="Ej. revisión contractual de contratos de obra" value={ofertas} onChange={(e) => setOfertas(e.target.value)} />
              </div>
              <div className="lg:col-span-2">
                <label className="field-label" htmlFor="estudio-notas">Notas y restricciones (opcional)</label>
                <textarea id="estudio-notas" rows={3} data-testid="estudio-notas-textarea" className="field resize-y" placeholder="Temas a evitar, fechas relevantes, énfasis deseado…" value={notas} onChange={(e) => setNotas(e.target.value)} />
              </div>
            </div>

            <button
              type="button"
              data-testid="estudio-generar-button"
              className="btn-primary mt-8"
              onClick={generar}
              disabled={generando}
            >
              {generando ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Generando piezas (puede tardar 1–2 minutos)…</>
              ) : (
                <><Sparkles className="h-4 w-4" /> Generar lote editorial</>
              )}
            </button>

            {error && (
              <div className="mt-6 flex items-start gap-2 border-l-2 border-destructive bg-destructive/5 p-4 text-sm text-destructive" data-testid="estudio-error-lote">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {supuestos.length > 0 && (
              <div className="mt-8 border border-hairline bg-parchment-surface/60 p-6" data-testid="estudio-supuestos">
                <p className="eyebrow mb-3">Supuestos declarados por el motor</p>
                <ul className="list-disc space-y-1.5 pl-5 text-sm text-ink-text">
                  {supuestos.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === "bandeja" && (
          <div data-testid="estudio-bandeja">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="font-serif text-3xl text-ink-deep sm:text-4xl">Bandeja de validación</h1>
              <select data-testid="estudio-filtro-estado" className="field max-w-xs" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="">Todos los estados</option>
                {ESTADOS.map((e) => <option key={e} value={e}>{e.replaceAll("_", " ")}</option>)}
              </select>
            </div>

            {piezas.length === 0 && (
              <p className="mt-10 border border-dashed border-hairline p-10 text-center text-ink-muted" data-testid="estudio-sin-piezas">
                No hay piezas con ese filtro. Genera un lote en "Nuevo encargo".
              </p>
            )}

            <div className="mt-8 grid gap-6 lg:grid-cols-5">
              <div className="lg:col-span-2">
                {piezas.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    data-testid={`estudio-pieza-${p.id}`}
                    onClick={() => { setSel(p); setNota(""); setError(""); setCopiado(false); }}
                    className={`block w-full border p-5 text-left transition-colors ${sel?.id === p.id ? "border-azul bg-azul-light/40" : "border-hairline bg-white hover:border-azul/40"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-serif text-lg leading-snug text-ink-deep">{p.tema}</p>
                      <Badge estado={p.estado} />
                    </div>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-ink-muted">
                      {p.canal} · {p.formato} · {p.momento_de_vida}
                    </p>
                  </button>
                ))}
              </div>

              {sel && (
                <div className="lg:col-span-3" data-testid="estudio-detalle">
                  <button type="button" data-testid="estudio-cerrar-detalle" className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink-deep lg:hidden" onClick={() => setSel(null)}>
                    <ArrowLeft className="h-4 w-4" /> Volver
                  </button>

                  <div className="border border-hairline bg-white p-6 sm:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <h2 className="font-serif text-2xl text-ink-deep">{sel.tema}</h2>
                      <Badge estado={sel.estado} />
                    </div>
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-wide text-salvia">{sel.gancho}</p>

                    <div className="mt-6 grid gap-4 text-sm text-ink-text sm:grid-cols-2">
                      <p><span className="text-ink-muted">Público:</span> {sel.publico}</p>
                      <p><span className="text-ink-muted">Servicio:</span> {sel.area_de_servicio || "—"}</p>
                      <p><span className="text-ink-muted">Momento:</span> {sel.momento_de_vida || "—"}</p>
                      <p><span className="text-ink-muted">Objetivo:</span> {sel.objetivo}</p>
                    </div>

                    {sel.verificacion?.afirmaciones_pendientes?.length > 0 && (
                      <div className="mt-6 border-l-2 border-azul bg-azul-light/40 p-4" data-testid="estudio-pendientes-fuentes">
                        <p className="text-xs font-bold uppercase tracking-wide text-azul">Pendiente de fuentes (nivel C)</p>
                        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-text">
                          {sel.verificacion.afirmaciones_pendientes.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}

                    <div className="mt-6">
                      <div className="flex items-center justify-between">
                        <p className="field-label">Contenido para publicar</p>
                        <button type="button" data-testid="estudio-copiar-button" className="inline-flex items-center gap-1.5 text-sm font-medium text-azul hover:underline" onClick={copiar}>
                          {copiado ? <><Check className="h-4 w-4" /> Copiado</> : <><Copy className="h-4 w-4" /> Copiar</>}
                        </button>
                      </div>
                      <div className="whitespace-pre-wrap border border-hairline bg-parchment-surface/40 p-5 text-[15px] leading-relaxed text-ink-text" data-testid="estudio-texto-publicacion">
                        {sel.texto_publicacion}
                      </div>
                    </div>

                    {sel.laminas.length > 0 && (
                      <div className="mt-5" data-testid="estudio-laminas">
                        <p className="field-label">Láminas del carrusel</p>
                        <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-text">
                          {sel.laminas.map((l, i) => <li key={i}>{l}</li>)}
                        </ol>
                      </div>
                    )}

                    {sel.guion.length > 0 && (
                      <div className="mt-5" data-testid="estudio-guion">
                        <p className="field-label">Guion del video</p>
                        <div className="whitespace-pre-wrap border border-hairline bg-parchment-surface/40 p-5 text-sm text-ink-text">{sel.guion.join("\n")}</div>
                      </div>
                    )}

                    <div className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
                      <div className="border border-hairline p-4" data-testid="estudio-visual">
                        <p className="text-xs font-bold uppercase tracking-wide text-salvia">Producción visual</p>
                        <p className="mt-2 text-ink-text">{sel.visual.descripcion || "—"}</p>
                        <p className="mt-2 text-ink-muted">Alt: {sel.visual.texto_alternativo || "—"}</p>
                      </div>
                      <div className="border border-hairline p-4" data-testid="estudio-cta">
                        <p className="text-xs font-bold uppercase tracking-wide text-salvia">CTA</p>
                        <p className="mt-2 text-ink-text">{sel.cta.texto || "—"}</p>
                        <p className="mt-1 font-mono text-xs text-ink-muted">
                          {sel.cta.url || "sin destino"} {sel.cta.destino_verificado ? "· destino verificado" : "· URL PENDIENTE"}
                        </p>
                        {sel.seo?.meta_description && <p className="mt-2 text-ink-muted">SEO: {sel.seo.meta_description}</p>}
                      </div>
                    </div>

                    <div className="mt-8 border-t border-hairline pt-6" data-testid="estudio-acciones">
                      <p className="field-label">Acción de validación</p>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <select
                          data-testid="estudio-accion-select"
                          className="field"
                          value=""
                          onChange={(e) => cambiarEstado(e.target.value)}
                          disabled={guardando}
                        >
                          <option value="">Cambiar a…</option>
                          {ESTADOS.filter((e) => e !== sel.estado).map((e) => (
                            <option key={e} value={e}>{e.replaceAll("_", " ")}{e === "APROBADO" ? " (requiere responsable)" : ""}</option>
                          ))}
                        </select>
                        <input
                          data-testid="estudio-responsable-input"
                          className="field"
                          placeholder="Responsable (para aprobar)"
                          value={responsable}
                          onChange={(ev) => setResponsable(ev.target.value)}
                        />
                      </div>
                      <textarea
                        data-testid="estudio-nota-textarea"
                        rows={2}
                        className="field mt-4 resize-y"
                        placeholder="Nota de revisión (opcional): qué falta o qué cambiar…"
                        value={nota}
                        onChange={(ev) => setNota(ev.target.value)}
                      />
                      {guardando && <p className="mt-3 inline-flex items-center gap-2 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Guardando…</p>}

                      {sel.historial.length > 0 && (
                        <div className="mt-6" data-testid="estudio-historial">
                          <p className="field-label">Historial</p>
                          <ul className="space-y-2 text-xs text-ink-muted">
                            {sel.historial.slice().reverse().map((h, i) => (
                              <li key={i}>
                                {h.de || "—"} → <span className="font-medium text-ink-text">{h.a}</span>
                                {h.responsable && ` · ${h.responsable}`}{h.nota && ` · ${h.nota}`}
                                {h.fecha && ` · ${h.fecha.slice(0, 10)}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
